import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import CssBaseline from "@mui/material/CssBaseline";
import { Loading, Store } from "./components/Ui";
import { axius, func } from "helpers";
import AppLayout from "./components/Layout/AppLayout";
import "translations/i18n";
import PrivateRoute from "navigation/PrivateRoute";
import FarmRequiredRoute from "navigation/FarmRequiredRoute";
import ActiveFarmRequiredRoute from "navigation/ActiveFarmRequiredRoute";
import ActiveCampaignRequiredRoute from "navigation/ActiveCampaignRequiredRoute";
import StockSheet from "pages/Stock/StockSheet";
import Dashboard from "pages/Dashboard";
import StockSteps from "pages/Stock/StockSteps";
import CashStockSheet from "pages/Stock/CashBook/CashStockSheet/";
import CahierSheet from "pages/Cahier/CahierSheet";
import { StepperProvider } from "components/Ui/Stepper";
import CaheirSteps from "pages/Cahier/CahierSteps";
import StockReport from "pages/Report/Stock/StockReport";
import ReportCalendar from "pages/Report/ReportCalendar";
import CahierReport from "pages/Report/Cahier/CahierReport";
import CahierReportDetails from "pages/Report/Cahier/CahierReportDetails";
import StockReportDetails from "pages/Report/Stock/StockReportDetails";
import { getFarms } from "services/farm.service";
import ForgotPassword from "pages/PasswordRecovery/ForgotPassword";
import OTP from "pages/PasswordRecovery/OTP";
import ResetPassword from "pages/PasswordRecovery/ResetPassword";
import { getUserProfile } from "services/auth.service";
import DefinePassword from "pages/PasswordDefine/DefinePassword";
import Formations from "pages/Formations";
import UserManuals from "pages/Footer/UserManuals";
import LegalNotice from "pages/Footer/LegalNotice";
import Contact from "pages/Footer/Contact";
import Refer from "pages/Footer/Recommendation";
import { client } from "services/api";
import axios from "axios";
import SignupAdmin from "features/accountCreation/SignupAdmin";
import ErrorBoundary from "./components/Layout/ErrorBoundary";
import Terms from "pages/Footer/Terms";
import DeteteAccount from "pages/Footer/DeleteAccount";
import Confidentials from "pages/Footer/confidentialities";
// ::: pages
const Home = React.lazy(() => import("./pages/Home"));
const Farms = React.lazy(() => import("./pages/Farms"));
const Login = React.lazy(() => import("./pages/Login"));
const NoPage = React.lazy(() => import("./pages/NoPage"));
const Signup = React.lazy(() => import("./pages/Signup"));
const Settings = React.lazy(() => import("./pages/Settings"));
const Operations = React.lazy(() => import("./pages/Operations"));
const Campaigns = React.lazy(() => import("./pages/Campaigns"));

const App = () => {
  const {
    state: {
      utils,
      userProfile,
      auth: { token, isAuth },
    },
    logout,
    setUtils,
    setFarmList,
    setUserProfile,
    setStatistics,
    statistics,
  } = React.useContext(Store);

  const getFarmsList = async (id) => {
    if (id) {
      const res = await getFarms(id);
      setFarmList(res);
    }
  };

  useEffect(() => {
    console.log(statistics);
  }, [statistics]);

  const toeknIsExpired = async (token) => {
    if (token) {
      const jwt = token;
      const jwtParts = jwt.split(".");
      const jwtPayload = JSON.parse(atob(jwtParts[1]));
      const expirationTimestamp = jwtPayload.exp * 1000; // Convert to milliseconds
      const currentDate = Date.now();
      return currentDate > expirationTimestamp;
    }
  };
  const getLoginUserDetails = async () => {
    const clientPersonnal = axios.create({
      baseURL: func.config.prod.url,
      headers: {
        Authorization: token,
      },
    });
    const res = await clientPersonnal.get("auth/getMyProfile");
    if (res.data.success) {
      setUserProfile(res.data.data);
      await getFarmsList(res.data.data.id);
    }
  };

  React.useEffect(() => {
    if (isAuth && token) {
      toeknIsExpired(token).then((response) => {
        if (response) {
          logout();
        } else {
          getLoginUserDetails();
        }
      });
    }
  }, [token, isAuth]);

  useEffect(() => {
    setUtils("loading", { spinning: true, primary: "" });
    Promise.all([
      axius.get("config/departmentCommunesArrondissements"),
      axius.get("statistic/getUser"),
    ]).then((res) => {
      setUtils("directory", res[0].success ? res[0].data : []);
      setStatistics(res[1].success ? res[1].data : []);
      setUtils("loading", { spinning: false, primary: "" });
    });
  }, []);

  return (
    <>
      <ErrorBoundary>
        <CssBaseline />
        <BrowserRouter>
          {utils.loading.spinning && <Loading />}
          <React.Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<AppLayout />}>
                <Route index element={<Home />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/otp" element={<OTP />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/signup/*" element={<Signup />} />
                <Route
                  path="/invite-users-prestataires/:passwordToken"
                  element={<DefinePassword />}
                />
                <Route path="/users-manuals" element={<UserManuals />} />
                <Route path="/legal-notice" element={<LegalNotice />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/refer" element={<Refer />} />
                <Route path="/delete-account" element={<DeteteAccount />} />

                <Route path="/terms-of-use" element={<Terms />} />
                <Route path="/privacy-policy" element={<Confidentials />} />
                <Route
                  path="/farms/*"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <Farms />
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/formations/*"
                  element={
                    <PrivateRoute>
                      <Formations />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/stock-report"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <StockReport />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/cahier/report-details"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <CahierReportDetails />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/report-calendar"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <ReportCalendar />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/cahier-report"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <CahierReport />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/stock/report-details"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <StockReportDetails />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <Settings />
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/operations/*"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <Operations />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/campaigns/*"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <Campaigns />
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route path="*" element={<NoPage />} />
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <Dashboard />
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/stock-sheet"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <StockSheet />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/stock-sheet/create-stock"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <StepperProvider>
                              <StockSteps />
                            </StepperProvider>
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/cahier-sheet"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <CahierSheet />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/cahier-sheet/cahier-steps"
                  element={
                    <StepperProvider>
                      <CaheirSteps />
                    </StepperProvider>
                  }
                />

                {/* <Route
                path="/stock-sheet/transection"
                element={<Transection />}
              />
                 <Route
                path="/stock-sheet/resume"
                element={<ResumeStep />}
              /> */}
                <Route
                  path="/stock-sheet/cashstocksheet"
                  element={
                    <PrivateRoute>
                      <FarmRequiredRoute>
                        <ActiveFarmRequiredRoute>
                          <ActiveCampaignRequiredRoute>
                            <CashStockSheet />
                          </ActiveCampaignRequiredRoute>
                        </ActiveFarmRequiredRoute>
                      </FarmRequiredRoute>
                    </PrivateRoute>
                  }
                />
              </Route>
            </Routes>
          </React.Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </>
  );
};

export default App;
